// src/components/dashboard/FiltersBar/ServiceCodeFilter.jsx
import React from 'react';
import { TextField, MenuItem, Box, alpha, useTheme, Typography, Chip } from '@mui/material';
import { Games, TrendingUp } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { setServiceCode } from '../../../store/slices/dashboardSlice';
import { useGetServiceCodesQuery } from '../../../store/api/dashboardApi';
import { formatCurrency } from '../../../utils/formatters';

const ServiceCodeFilter = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { serviceCode } = useSelector((state) => state.dashboard.filters);
  const { data: serviceCodes = [], isLoading, error } = useGetServiceCodesQuery();

  const handleChange = (event) => {
    dispatch(setServiceCode(event.target.value));
  };

  // Format service codes for display
  const formatServiceCodes = () => {
    if (!serviceCodes.length) return [];
    
    return serviceCodes.map(service => ({
      ...service,
      displayLabel: service.service_code === 'all' 
        ? 'All Services' 
        : `${service.service_code} (${service.bet_count} bets)`,
    }));
  };

  const formattedServiceCodes = formatServiceCodes();
  const selectedService = formattedServiceCodes.find(s => s.value === serviceCode);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Games sx={{ color: 'primary.main', fontSize: '1rem' }} />
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.875rem'
          }}
        >
          Service Filter
        </Typography>
        {selectedService && selectedService.value !== '' && (
          <Chip
            size="small"
            label={selectedService.label}
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>

      <TextField
        select
        value={serviceCode}
        onChange={handleChange}
        size="medium"
        disabled={isLoading}
        fullWidth
        placeholder="Select service..."
        sx={{ 
          minWidth: { xs: '100%', sm: 250 },
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            background: alpha(theme.palette.background.paper, 0.7),
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.2),
              transition: 'all 0.3s ease',
            },
            '&:hover fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.4),
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
          },
          '& .MuiInputLabel-root': {
            color: 'text.secondary',
            fontWeight: 600,
          },
          '& .MuiSelect-select': {
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }
        }}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            if (!selected) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  <Games sx={{ fontSize: '1rem' }} />
                  All Services
                </Box>
              );
            }
            const service = formattedServiceCodes.find(s => s.value === selected);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Games sx={{ fontSize: '1rem', color: 'primary.main' }} />
                {service?.label || selected}
              </Box>
            );
          },
          MenuProps: {
            PaperProps: {
              sx: {
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                backdropFilter: 'blur(10px)',
                maxHeight: 300,
                '& .MuiMenuItem-root': {
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                  '&.Mui-selected': {
                    background: alpha(theme.palette.primary.main, 0.2),
                    fontWeight: 600,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.3),
                    },
                  },
                },
              },
            },
          },
        }}
      >
        {isLoading && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Loading services...
            </Typography>
          </MenuItem>
        )}
        
        {error && (
          <MenuItem disabled>
            <Typography variant="body2" color="error">
              Failed to load services
            </Typography>
          </MenuItem>
        )}
        
        {formattedServiceCodes.map((service) => (
          <MenuItem key={service.value} value={service.value}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Games sx={{ 
                fontSize: '1rem', 
                color: service.value === '' ? 'text.secondary' : 'primary.main' 
              }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {service.label}
                </Typography>
                {service.value !== '' && service.bet_count !== 'All' && (
                  <Typography variant="caption" color="text.secondary">
                    {service.bet_count} bets • {formatCurrency(parseFloat(service.total_amount || 0))}
                  </Typography>
                )}
              </Box>
              {service.value !== '' && service.bet_count !== 'All' && (
                <TrendingUp sx={{ fontSize: '0.875rem', color: 'success.main' }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

export default ServiceCodeFilter;


// // src/components/dashboard/FiltersBar/ServiceCodeFilter.jsx
// import React from 'react';
// import { TextField, MenuItem, Box, alpha, useTheme } from '@mui/material';
// import { useSelector, useDispatch } from 'react-redux';
// import { setServiceCode } from '../../../store/slices/dashboardSlice';
// import { useGetServiceCodesQuery } from '../../../store/api/dashboardApi';

// const ServiceCodeFilter = () => {
//   const theme = useTheme();
//   const dispatch = useDispatch();
//   const { serviceCode } = useSelector((state) => state.dashboard.filters);
//   const { data: serviceCodes = [], isLoading } = useGetServiceCodesQuery();

//   const handleChange = (event) => {
//     dispatch(setServiceCode(event.target.value));
//   };

//   return (
//     <Box>
//       <TextField
//         select
//         value={serviceCode}
//         onChange={handleChange}
//         size="medium"
//         disabled={isLoading}
//         fullWidth
//         sx={{ 
//           minWidth: { xs: '100%', sm: 200 },
//           '& .MuiOutlinedInput-root': {
//             borderRadius: 3,
//             background: alpha(theme.palette.background.paper, 0.7),
//             backdropFilter: 'blur(10px)',
//             transition: 'all 0.3s ease',
//             '& fieldset': {
//               borderColor: alpha(theme.palette.primary.main, 0.2),
//               transition: 'all 0.3s ease',
//             },
//             '&:hover fieldset': {
//               borderColor: alpha(theme.palette.primary.main, 0.4),
//               boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
//             },
//             '&.Mui-focused fieldset': {
//               borderColor: theme.palette.primary.main,
//               boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
//             },
//           },
//           '& .MuiInputLabel-root': {
//             color: 'text.secondary',
//             fontWeight: 600,
//           },
//           '& .MuiSelect-select': {
//             fontWeight: 600,
//           }
//         }}
//         SelectProps={{
//           displayEmpty: true,
//           MenuProps: {
//             PaperProps: {
//               sx: {
//                 borderRadius: 2,
//                 border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
//                 backdropFilter: 'blur(10px)',
//                 '& .MuiMenuItem-root': {
//                   borderRadius: 1,
//                   mx: 1,
//                   my: 0.5,
//                   transition: 'all 0.2s ease',
//                   '&:hover': {
//                     background: alpha(theme.palette.primary.main, 0.1),
//                   },
//                   '&.Mui-selected': {
//                     background: alpha(theme.palette.primary.main, 0.2),
//                     fontWeight: 600,
//                   },
//                 },
//               },
//             },
//           },
//         }}
//         placeholder="Select service"
//       >
//         {serviceCodes.map((option) => (
//           <MenuItem key={option.value} value={option.value}>
//             {option.label}
//           </MenuItem>
//         ))}
//       </TextField>
//     </Box>
//   );
// };

// export default ServiceCodeFilter;

// // src/components/dashboard/FiltersBar/ServiceCodeFilter.jsx
// import React from 'react';
// import { TextField, MenuItem, Box, Typography } from '@mui/material';
// import { useSelector, useDispatch } from 'react-redux';
// import { setServiceCode } from '../../../store/slices/dashboardSlice';
// import { useGetServiceCodesQuery } from '../../../store/api/dashboardApi';

// const ServiceCodeFilter = () => {
//   const dispatch = useDispatch();
//   const { serviceCode } = useSelector((state) => state.dashboard.filters);
//   const { data: serviceCodes = [], isLoading } = useGetServiceCodesQuery();

//   const handleChange = (event) => {
//     dispatch(setServiceCode(event.target.value));
//   };

//   return (
//     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//       <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
//         Service Code
//       </Typography>
      
//       <TextField
//         select
//         value={serviceCode}
//         onChange={handleChange}
//         size="small"
//         disabled={isLoading}
//         sx={{ minWidth: { xs: '100%', sm: 200 } }}
//         SelectProps={{
//           displayEmpty: true,
//         }}
//       >
//         {serviceCodes.map((option) => (
//           <MenuItem key={option.value} value={option.value}>
//             {option.label}
//           </MenuItem>
//         ))}
//       </TextField>
//     </Box>
//   );
// };

// export default ServiceCodeFilter;